import React from 'react';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { ServiceRenewalReminderListItem } from './useData';
import { APP_CONFIG, formatDate as formatDateGlobal, formatDateTime as formatDateTimeGlobal } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import { Badge, StandardTableActions } from '../../components/ui';

/** Hex colors for renewal response status badges (aligned with app status-badge style). */
const RENEWAL_STATUS_COLOR: Record<string, string> = {
  pending: '#f59e0b',
  renewed: '#10b981',
  declined: '#ef4444',
  no_response: '#64748b',
  will_proceed: '#3b82f6',
};

const RENEWAL_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  renewed: 'Renewed',
  declined: 'Declined',
  no_response: 'No response',
  will_proceed: 'Will proceed',
};

export const renderRenewalStatusBadge = (status?: string | null) => {
  const key = (status || 'pending').toLowerCase();
  const label = RENEWAL_STATUS_LABEL[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—');
  const hex = RENEWAL_STATUS_COLOR[key] ?? RENEWAL_STATUS_COLOR.no_response;
  return <Badge color={hex}>{label}</Badge>;
};

export const moduleConfig = {
  module: {
    title: 'Service Renewal Reminders',
    description: 'List of service renewal reminders and the services they belong to',
    permissionModuleKey: 'client_services',
    route: {
      list: '/service_renewal_reminders',
      new: '/client-services',
      detail: (id: number) => `/client-services/${id}`,
      edit: (id: number) => `/client-services/${id}/edit`,
    },
    api: {
      list: '/client-service-renewal-reminders/list',
      detail: (id: number) => `/client-service-renewal-reminders/${id}`,
    },
  },

  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  search: {
    placeholder: 'Search by service, client, or status...',
  },

  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['renewalResponseStatus', 'sentAt', 'clientName', 'serviceDescr'],
    },
  ],

  filters: {
    fields: [
      {
        key: 'renewalResponseStatus',
        label: 'Status',
        placeholder: 'All Status',
        type: 'select' as const,
        getValue: (item: ServiceRenewalReminderListItem) => item.renewalResponseStatus || '',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'renewed', label: 'Renewed' },
          { value: 'will_proceed', label: 'Will proceed' },
          { value: 'declined', label: 'Declined' },
          { value: 'no_response', label: 'No response' },
        ],
      },
      {
        key: 'sentAt',
        label: 'Sent date',
        placeholder: 'All dates',
        type: 'date' as const,
        getValue: (item: ServiceRenewalReminderListItem) =>
          item.sentAt ? new Date(item.sentAt).toLocaleDateString() : undefined,
        options: [],
      },
      {
        key: 'clientName',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'text' as const,
        getValue: (item: ServiceRenewalReminderListItem) =>
          item.clientService?.client?.clientName ?? '',
        options: [],
      },
      {
        key: 'serviceDescr',
        label: 'Service',
        placeholder: 'All Services',
        type: 'text' as const,
        getValue: (item: ServiceRenewalReminderListItem) =>
          item.clientService?.serviceDescr ?? '',
        options: [],
      },
    ],
    applyFilter: (
      items: ServiceRenewalReminderListItem[],
      filters: {
        renewalResponseStatus: string[];
        sentAt: string[];
        clientName: string[] | string;
        serviceDescr: string[] | string;
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        if (filters.renewalResponseStatus?.length) {
          const status = (item.renewalResponseStatus || '').toLowerCase();
          const match = filters.renewalResponseStatus.some(
            (f) => f.toLowerCase() === status
          );
          if (!match) return false;
        }
        if (filters.sentAt?.length && ranges.sentAt) {
          const range = ranges.sentAt;
          const sent = item.sentAt ? new Date(item.sentAt) : null;
          if (sent && range.start && range.end && (sent < range.start || sent > range.end))
            return false;
        }
        const clientFilter = filters.clientName;
        if (typeof clientFilter === 'string' && clientFilter.trim() !== '') {
          const name = (item.clientService?.client?.clientName ?? '').toLowerCase();
          if (!name.includes(clientFilter.toLowerCase())) return false;
        }
        const serviceFilter = filters.serviceDescr;
        if (typeof serviceFilter === 'string' && serviceFilter.trim() !== '') {
          const desc = (item.clientService?.serviceDescr ?? '').toLowerCase();
          if (!desc.includes(serviceFilter.toLowerCase())) return false;
        }
        return true;
      });
    },
  },

  fields: [
    { name: 'renewalResponseStatus', label: 'Status', type: 'select' },
    { name: 'sentAt', label: 'Sent date', type: 'date' },
    { name: 'clientName', label: 'Client', type: 'text' },
    { name: 'serviceDescr', label: 'Service', type: 'text' },
  ],

  toolbar: {
    viewToggle: {
      enabled: true,
      views: [
        { key: 'list', label: 'List', icon: 'list', href: '/client-services' },
        { key: 'renewal', label: 'Renewal Reminders', icon: 'reminder', href: '/service_renewal_reminders' },
        { key: 'yearly', label: 'Services', icon: 'chart', href: '/client-services-yearly' },
      ],
    },
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) return;
              const { exportToExcel } = await import('../../components/exports');
              await exportToExcel(exportData);
            },
          },
        ],
      },
    ],
  },

  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ServiceRenewalReminderListItem) => {
      const id = item.clientService?.clientServicetId;
      if (id) window.location.href = `/client-services/${id}`;
    },
    keyExtractor: (item: ServiceRenewalReminderListItem) => item.id,
  },

  emptyStates: {
    noData: 'No renewal reminders yet. Reminders are created from Client Services.',
    noSearchResults: 'No reminders match your search.',
    noFilterResults: 'No reminders match the selected filters.',
  },

  delete: {
    title: 'Delete reminder',
    message: 'Are you sure you want to delete this renewal reminder?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete reminder.',
    handler: async (id: number) => {
      const api = (await import('../../services/api')).default;
      await api.delete(`/client-service-renewal-reminders/${id}`);
    },
  },

  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString) || '-';
    },
    formatDateTime: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateTimeGlobal(dateString) || '-';
    },
  },

  getColumns: (
    formatDate: (dateString?: string) => string,
    colors?: ThemeColors
  ): Column<ServiceRenewalReminderListItem>[] => {
    const formatDateTime = (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateTimeGlobal(dateString) || '-';
    };
    const textMuted = colors?.textMuted || '';

    return [
      {
        key: 'sentAt',
        header: 'Sent',
        sortable: true,
        render: (value) => formatDateTime(value),
      },
      {
        key: 'reminderNumber',
        header: '#',
        sortable: true,
        render: (value) => (value != null ? String(value) : <span className={`text-sm ${textMuted}`}>-</span>),
      },
      {
        key: 'channel',
        header: 'Channel',
        sortable: true,
        render: (value) => (value ? String(value) : <span className={`text-sm ${textMuted}`}>-</span>),
      },
      {
        key: 'renewalResponseStatus',
        header: 'Status',
        sortable: true,
        render: (_value, row) => renderRenewalStatusBadge(row.renewalResponseStatus),
      },
      {
        key: 'renewalResponseAt',
        header: 'Response date',
        sortable: true,
        render: (value) => (value ? formatDateTime(value) : <span className={`text-sm ${textMuted}`}>-</span>),
      },
      {
        key: 'serviceDescr',
        header: 'Service',
        sortable: false,
        render: (_value, row) => {
          const serviceText = row.clientService?.serviceDescr ?? '-';
          const clientName = row.clientService?.client?.clientName ?? '';
          return (
            <div className="min-w-0">
              <div>
                <TruncatedText text={serviceText} maxLength={50} showTooltip={true} />
              </div>
              {clientName && (
                <div className={`mt-0.5 text-xs truncate ${textMuted}`}>{clientName}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'serviceStartDate',
        header: 'Start date',
        sortable: false,
        render: (_value, row) =>
          row.clientService?.serviceStartDate
            ? formatDate(row.clientService.serviceStartDate)
            : <span className={`text-sm ${textMuted}`}>-</span>,
      },
      {
        key: 'serviceEndDate',
        header: 'End date',
        sortable: false,
        render: (_value, row) =>
          row.clientService?.serviceEndDate
            ? formatDate(row.clientService.serviceEndDate)
            : <span className={`text-sm ${textMuted}`}>-</span>,
      },
      {
        key: 'serviceCost',
        header: 'Cost',
        sortable: false,
        render: (_value, row) => {
          const cost = row.clientService?.serviceCost;
          if (cost == null) return <span className={`text-sm ${textMuted}`}>-</span>;
          return <span className={`text-sm ${colors?.textSecondary || ''}`}>{Number(cost).toLocaleString()}</span>;
        },
      },
      {
        key: 'serviceInvoice',
        header: 'Invoice',
        sortable: false,
        render: (_value, row) => {
          const inv = row.clientService?.serviceInvoice;
          if (inv == null) return <span className={`text-sm ${textMuted}`}>-</span>;
          return <span className={`text-sm ${colors?.textSecondary || ''}`}>{Number(inv).toLocaleString()}</span>;
        },
      },
    ];
  },

  getToolbarIcon: (iconName: string): React.ReactNode => {
    const iconClass = 'w-5 h-5';
    const svgProps = { className: iconClass, fill: 'none' as const, stroke: 'currentColor' as const, viewBox: '0 0 24 24' };
    if (iconName === 'download') {
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    }
    if (iconName === 'list') {
      return <svg {...svgProps}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>;
    }
    if (iconName === 'reminder') {
      return <svg {...svgProps}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>;
    }
    if (iconName === 'chart') {
      return <svg {...svgProps}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
    }
    return null;
  },

  getTableActions: (
    item: ServiceRenewalReminderListItem,
    onDelete: (id: number) => void,
    colors?: unknown,
    _listContext?: unknown,
    options?: { canDelete?: boolean }
  ): React.ReactNode => {
    const canDelete = options?.canDelete !== false;
    const serviceId = item.clientService?.clientServicetId;

    return (
      <StandardTableActions
        viewTo={serviceId != null ? `/client-services/${serviceId}` : undefined}
        onDelete={() => onDelete(item.id)}
        canView={serviceId != null}
        canEdit={false}
        canDelete={canDelete}
      />
    );
  },
};

export const getColumns = moduleConfig.getColumns;
export const getToolbarIcon = moduleConfig.getToolbarIcon;
export const getTableActions = moduleConfig.getTableActions;
